import { BrowserSpeech, chooseMode } from "./fallback.js";
import type { BrowserSpeechEnv, UiLanguage, VoiceMode } from "./fallback.js";
import { MicDeniedError, createMicCapture } from "./mic.js";
import type { MicCapture } from "./mic.js";
import { PcmPlayer, speakWithServer } from "./playback.js";
import type { PlayerAudioContext } from "./playback.js";
import { openSttSession, sttUrl } from "./stt-client.js";
import type { SttLanguage, SttSession, SttSocketCtor } from "./stt-client.js";

export { BrowserSpeech, bcp47, chooseMode } from "./fallback.js";
export type { UiLanguage, VoiceMode } from "./fallback.js";
export { MicDeniedError, createMicCapture } from "./mic.js";
export { PcmPlayer, TTS_PATH, TtsError, speakWithServer } from "./playback.js";
export { STT_PATH, SttError, openSttSession, parseSttEvent, sttUrl } from "./stt-client.js";
export type { SttEvent, SttLanguage } from "./stt-client.js";
export { FrameBuffer, PcmDecoder, Resampler, STT_SAMPLE_RATE, floatToInt16 } from "./pcm.js";

/** Gateway route answering `{ available: boolean }` for the server voice path. */
export const VOICE_STATUS_PATH = "/api/v1/voice/status";

export type VoiceErrorKind = "micDenied" | "failed" | "unavailable";

export interface VoiceState {
  mode: VoiceMode;
  listening: boolean;
  transcribing: boolean;
  speaking: boolean;
  error: VoiceErrorKind | null;
}

export interface VoiceControllerOptions {
  /** Bearer session token for REST; null = the session cookie is used. */
  token(): string | null;
  /** The user's `voice.language` setting; an explicit language is the fast STT path. */
  language(): SttLanguage;
  /** UI language, used by the browser fallback when `language()` is `auto`. Default `tr`. */
  uiLanguage?(): UiLanguage;
  /** The selected server voice, if any (the gateway uses the user's selection otherwise). */
  voiceId?(): string | null;
  /** Prefix for gateway URLs. Default "" (same origin). */
  baseUrl?: string;
  fetch?: typeof fetch;
  WebSocket?: SttSocketCtor;
  AudioContext?: typeof AudioContext;
  mediaDevices?: Pick<MediaDevices, "getUserMedia">;
  speechEnv?: BrowserSpeechEnv;
  location?: Pick<Location, "protocol" | "host">;
}

export interface VoiceController {
  readonly state: VoiceState;
  readonly mode: VoiceMode;
  /** Re-checks `GET /api/v1/voice/status` and picks the input mode. */
  refresh(): Promise<VoiceMode>;
  /** Push-to-talk: starts listening; resolves with the transcript after `stopListening()`. */
  listen(): Promise<string>;
  stopListening(): void;
  /** Speaks with the server voice, falling back to the browser voice on any server error. */
  speak(text: string): Promise<void>;
  /** Barge-in: stops playback at once. */
  stopSpeaking(): void;
  subscribe(listener: (state: VoiceState) => void): () => void;
  dispose(): void;
}

const asError = (err: unknown): Error =>
  err instanceof Error ? err : new Error(typeof err === "string" ? err : "voice failed");

export function createVoiceController(opts: VoiceControllerOptions): VoiceController {
  const doFetch = opts.fetch ?? ((...args: Parameters<typeof fetch>) => globalThis.fetch(...args));
  const browser = new BrowserSpeech(opts.speechEnv);
  const AudioCtx = opts.AudioContext ?? globalThis.AudioContext;
  const devices = opts.mediaDevices ?? globalThis.navigator?.mediaDevices;
  const canCapture = !!AudioCtx && typeof devices?.getUserMedia === "function";
  const player = new PcmPlayer((): PlayerAudioContext => new AudioCtx());
  const listeners = new Set<(s: VoiceState) => void>();
  let serverAvailable = false;
  let state: VoiceState = {
    mode: chooseMode({ serverAvailable, canCapture, canRecognize: browser.canRecognize }),
    listening: false,
    transcribing: false,
    speaking: false,
    error: null,
  };
  let stopCurrent: (() => void) | null = null;
  let speakAbort: AbortController | null = null;

  const set = (patch: Partial<VoiceState>) => {
    state = { ...state, ...patch };
    for (const l of listeners) l(state);
  };
  const uiLang = (): UiLanguage => {
    const l = opts.language();
    return l === "auto" ? (opts.uiLanguage?.() ?? "tr") : l;
  };
  const authHeaders = (): Record<string, string> => {
    const t = opts.token();
    return t ? { authorization: `Bearer ${t}` } : {};
  };
  const pickMode = () =>
    set({ mode: chooseMode({ serverAvailable, canCapture, canRecognize: browser.canRecognize }) });

  const listenServer = (): Promise<string> => {
    let session: SttSession | null = null;
    let mic: MicCapture | null = null;
    const started = (async () => {
      session = openSttSession({
        url: sttUrl(opts.language(), opts.location ?? globalThis.location),
        token: opts.token(),
        ...(opts.WebSocket ? { WebSocket: opts.WebSocket } : {}),
      });
      const s = session;
      mic = createMicCapture({
        onFrame: (f) => s.sendPcm(f),
        ...(opts.AudioContext ? { AudioContext: opts.AudioContext } : {}),
        ...(devices ? { mediaDevices: devices } : {}),
      });
      await mic.start();
    })();
    return new Promise<string>((resolve, reject) => {
      started.catch((err: unknown) => {
        session?.abort();
        stopCurrent = null;
        reject(asError(err));
      });
      stopCurrent = () => {
        stopCurrent = null;
        void started
          .then(async () => {
            await mic?.stop();
            set({ listening: false, transcribing: true });
            return session!.end();
          })
          .then(resolve, (err: unknown) => {
            // The server path broke: use the browser next time, if it can recognize.
            serverAvailable = false;
            pickMode();
            reject(asError(err));
          });
      };
    });
  };

  const listenBrowser = (): Promise<string> => {
    const rec = browser.recognize(uiLang());
    stopCurrent = () => {
      stopCurrent = null;
      set({ listening: false, transcribing: true });
      rec.stop();
    };
    return rec.result;
  };

  const controller: VoiceController = {
    get state() {
      return state;
    },
    get mode() {
      return state.mode;
    },
    async refresh() {
      try {
        const res = await doFetch(`${opts.baseUrl ?? ""}${VOICE_STATUS_PATH}`, {
          headers: authHeaders(),
        });
        const body = (res.ok ? await res.json().catch(() => null) : null) as {
          available?: unknown;
        } | null;
        serverAvailable = body?.available === true;
      } catch {
        serverAvailable = false;
      }
      pickMode();
      return state.mode;
    },
    async listen() {
      if (state.listening) throw new Error("already listening");
      controller.stopSpeaking();
      const mode = state.mode;
      if (mode === "none") {
        set({ error: "unavailable" });
        throw new Error("voice input unavailable");
      }
      set({ listening: true, transcribing: false, error: null });
      try {
        const text = await (mode === "server" ? listenServer() : listenBrowser());
        set({ listening: false, transcribing: false });
        return text;
      } catch (err) {
        set({
          listening: false,
          transcribing: false,
          error: err instanceof MicDeniedError ? "micDenied" : "failed",
        });
        throw err;
      }
    },
    stopListening() {
      stopCurrent?.();
    },
    async speak(text) {
      const clean = text.trim();
      if (!clean) return;
      controller.stopSpeaking();
      const ac = new AbortController();
      speakAbort = ac;
      set({ speaking: true });
      try {
        if (serverAvailable && AudioCtx) {
          try {
            await speakWithServer({
              text: clean,
              token: opts.token(),
              voiceId: opts.voiceId?.() ?? null,
              language: uiLang(),
              player,
              fetch: doFetch,
              ...(opts.baseUrl ? { baseUrl: opts.baseUrl } : {}),
              signal: ac.signal,
            });
            return;
          } catch {
            if (ac.signal.aborted) return;
            player.stop();
          }
        }
        if (browser.canSynthesize && !ac.signal.aborted) {
          await browser.speak(clean, uiLang());
        }
      } finally {
        if (speakAbort === ac) {
          speakAbort = null;
          set({ speaking: false });
        }
      }
    },
    stopSpeaking() {
      speakAbort?.abort();
      speakAbort = null;
      player.stop();
      browser.cancel();
      if (state.speaking) set({ speaking: false });
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    dispose() {
      controller.stopSpeaking();
      stopCurrent?.();
      listeners.clear();
      void player.close();
    },
  };
  return controller;
}
